import React, { useState } from "react";
import { Stack } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Form, FormGroup, InputGroup, InputGroupText, Alert } from "reactstrap";
import { Controller, useForm } from "react-hook-form";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Mail01Icon,
  SentIcon,
  AlertCircleIcon,
} from "@hugeicons/core-free-icons";
import { AntInput } from "../../CommonElements/AntInput";
import { Divider, Space } from "antd";
import { Link } from "react-router-dom";
import { useForgetPasswordMutation } from "../../services/useAuth/useAuthMutation";

export const ForgetPassword = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const { t } = useTranslation();
  const { mutate: forgetPasswordMutation, isPending } =
    useForgetPasswordMutation();

  const onSubmit = (data) => {
    setErrorMessage("");
    setSubmittedEmail(data.email);
    forgetPasswordMutation(data, {
      onSuccess: () => {
        setIsSuccess(true);
        reset();
      },
      onError: (error) => {
        setIsSuccess(false);
        // Check if it's a user not found error
        if (
          error?.response?.status === 404 ||
          error?.message?.toLowerCase().includes("user not found") ||
          error?.message?.toLowerCase().includes("email not found")
        ) {
          setErrorMessage(t("USER_NOT_FOUND_ERROR"));
        } else {
          setErrorMessage(error?.message || t("FORGOT_PASSWORD_FAILED"));
        }
      },
    });
  };

  // Function to get email provider URL
  const getEmailProviderUrl = (email) => {
    const domain = email.split("@")[1]?.toLowerCase();
    const providerUrls = {
      "gmail.com": "https://mail.google.com",
      "outlook.com": "https://outlook.live.com",
      "hotmail.com": "https://outlook.live.com",
      "yahoo.com": "https://mail.yahoo.com",
      "icloud.com": "https://www.icloud.com/mail",
      "aol.com": "https://mail.aol.com",
    };
    return providerUrls[domain] || null;
  };

  const emailProviderUrl = submittedEmail
    ? getEmailProviderUrl(submittedEmail)
    : null;

  // Success state UI
  if (isSuccess) {
    return (
      <div className="login-card1 decorative-arabic-text-bg h-100">
        <div className="theme-form login-form pattern-card rounded-3 border border-1">
          <div className="text-center mb-4">
            {/* <HugeiconsIcon
              icon={}
              size={64}
              className="text-success mb-3"
            /> */}
            <h4 className="text-success">{t("EMAIL_SENT_SUCCESSFULLY")}</h4>
            <p className="text-muted mb-3">
              {t("PASSWORD_RESET_EMAIL_SENT_TO")}{" "}
              <strong>{submittedEmail}</strong>
            </p>
            {
              <Trans
                i18nKey="CHECK_EMAIL_INSTRUCTIONS"
                components={{
                  a: emailProviderUrl ? (
                    <a
                      className="fw-bold"
                      href={emailProviderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ) : (
                    <span />
                  ),
                }}
              />
            }
          </div>

          <Divider />

          <div className="text-center">
            <Link to="/login">{t("BACK_TO_LOGIN")}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-card1 decorative-arabic-text-bg h-100">
      <Form
        className="theme-form login-form pattern-card rounded-3 border border-1"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack gap={3}>
          <h4>{t("FORGOT_PASSWORD")}</h4>
          <h6 className="fw-bold">{t("FORGOT_PASSWORD_DESCRIPTION")}</h6>
        </Stack>

        {errorMessage && (
          <div className="alert alert-danger-outline text-danger gap-2 border border-danger rounded-3 mb-3 d-flex align-items-center">
            <div className="d-flex align-items-center">
              <HugeiconsIcon icon={AlertCircleIcon} className="me-2" />
              <div>
                <strong>{t("ERROR")}:</strong> {t(errorMessage)}
              </div>
            </div>
          </div>
        )}

        <FormGroup>
          <Controller
            name="email"
            control={control}
            rules={{
              required: t("EMAIL_REQUIRED"),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t("INVALID_EMAIL_FORMAT"),
              },
            }}
            render={({ field }) => (
              <>
                <InputGroup>
                  <InputGroupText>
                    <HugeiconsIcon icon={Mail01Icon} />
                  </InputGroupText>
                  <AntInput
                    type="text"
                    placeholder={t("EMAIL_ADDRESS")}
                    {...field}
                  />
                </InputGroup>

                {errors.email && (
                  <div className="text-danger small mt-1">
                    {errors.email.message}
                  </div>
                )}
              </>
            )}
          />
        </FormGroup>
        <FormGroup>
          <button
            disabled={isPending}
            type="submit"
            className="btn btn-primary m-0 w-100"
          >
            <Space align="center">
              <HugeiconsIcon className="d-flex" size={16} icon={SentIcon} />
              {isPending ? t("SENDING") : t("SEND")}
            </Space>
          </button>
        </FormGroup>
        <Divider />
        <div className="text-center">
          <Link to="/login" className="link fw-bold">
            {t("LOGIN")}
          </Link>
        </div>
      </Form>
    </div>
  );
};
